import { Api } from 'api/common.generated';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, Canceler, CancelToken, InternalAxiosRequestConfig } from 'axios';

import OffersApiWrapper from 'features/offers/api/offers.api';
import { API_BASE_URL, currentLanguage } from 'shared/constants/common';
import { findCookieValue } from 'shared/util/utility';
import BestSellerApiWrapper from './best-seller.api';
import BetMatchApiWrapper from './betMatch.api';
import BetslipAnalyticsApiWrapper from './betslipAnalytics.api';
import BettingApiWrapper from './bettingApi';
import CategoriesApiWrapper from './categories.api';
import CMSApiWrapper from './cms.api';
import CustomerApiWrapper from './customer.api';
import DailyChallengeApiWrapper from './daily-challenge.api';
import { GiifTypesApiWrapper } from './giifTypes';
import IntegrationApiWrapper from './integration.api';
import KuponKibicaWrapper from './kupon-kibica.api';
import LiveMatchApiWrapper from './live-api';
import LiveBettingPopularApiWrapper from './live-betting-popular.api';
import LoginApiWrapper from './login.api';
import { AuthologicApiWrapper } from './mojeIdRegistration.api';
import MyBetApiWrapper from './my-bet.api';
import PasswordApiWrapper from './password.api';
import playerProfileApiWrapper from './player-profile.api';
import PromoCodeWrapper from './promo-code.api';
import RefreshConfigApiWrapper from './refreshConfig.api';
import RegistrationApiWrapper from './registration.api';
import SpecialCategoriesApiWrapper from './specialCategories';
import SportsApiWrapper from './sport.api';
import TransactionApiWrapper from './transaction.api';

/**
 * store all API's cancel tokens
 */
const cancelReqObj: Record<string, Canceler> = {};

/**
 * To cancel request
 * @param {string} url URL to cancel request
 * @param {string} message cancel request message
 */
export const cancelRequest = (url: string, message?: string) => {
	const canceler = cancelReqObj[url];
	if (canceler) {
		canceler(message);
	}
};

type ResponseDataType<T = any> = {
	code: number;
	data: T;
	isError: boolean;
	message?: string;
};

export interface RequestsInstance {
	delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
	get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ResponseDataType<T>>>;
	getRaw<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
	post<T = any>(url: string, body?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
	put<T = any>(url: string, body?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
}

/**
 * To get cancel token for the API
 * @param {(string | undefined)} url API url
 * @returns {undefined | CancelToken} cancel token to cancel request
 */
function getCancelToken(url: string | undefined): undefined | CancelToken {
	if (!url) {
		return;
	}
	const tokenSource = axios.CancelToken.source();
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	cancelReqObj[url.split('?')[0]!] = tokenSource.cancel;
	return tokenSource.token;
}

function requestInterceptor(config: InternalAxiosRequestConfig) {
	config.headers['Request-Language'] = config.headers['Request-Language'] || currentLanguage;
	config.headers['x-odds-session'] = config.headers['x-odds-session'] || findCookieValue('x-odds-session');

	const element = document.querySelector('html');
	if (element) {
		config.headers['B-Device-Type'] = element.getAttribute('device-type');
		config.headers['B-Client-Type'] = element.getAttribute('client-type');
	}

	if (!config.cancelToken) {
		const token = getCancelToken(config.url);
		if (token) {
			config.cancelToken = token;
		}
	}

	return config;
}

function requests(this: AxiosInstance): RequestsInstance {
	this.interceptors.request.use(requestInterceptor);
	return {
		delete: (url: string, config?: AxiosRequestConfig) => {
			return this.delete(url, config);
		},
		get: (url: string, config?: AxiosRequestConfig) => {
			return this.get(url, config);
		},
		getRaw: (url: string, config?: AxiosRequestConfig) => {
			return this.get(url, config);
		},
		post: (url: string, body: any, config?: AxiosRequestConfig) => {
			return this.post(url, body, config);
		},
		put: (url: string, body: any, config?: AxiosRequestConfig) => {
			return this.put(url, body, config);
		},
	};
}

// legacy API
const baseInstance = axios.create({ baseURL: API_BASE_URL });

// new API (generated from the swagger)
const CommonAPI = new Api({ baseURL: API_BASE_URL?.replace(/\/api\/v1$/, '') });
CommonAPI.instance.interceptors.request.use(requestInterceptor);

const CategoriesApi = CategoriesApiWrapper(requests.call(baseInstance));
const LiveApi = LiveMatchApiWrapper(requests.call(baseInstance));
const BestSeller = BestSellerApiWrapper(requests.call(baseInstance));
const LiveBetting = LiveBettingPopularApiWrapper(requests.call(baseInstance));
const LoginApi = LoginApiWrapper(requests.call(baseInstance));
const BettingApi = BettingApiWrapper(requests.call(baseInstance));
const RegistrationApi = RegistrationApiWrapper(requests.call(baseInstance));
const AuthologicApi = AuthologicApiWrapper(requests.call(baseInstance));
const GiifTypesApi = GiifTypesApiWrapper(requests.call(baseInstance));
const PasswordApi = PasswordApiWrapper(requests.call(baseInstance));
const CMSApi = CMSApiWrapper(requests.call(baseInstance));
const CustomerApi = CustomerApiWrapper(requests.call(baseInstance));
const OffersApi = OffersApiWrapper(requests.call(baseInstance));
const TransactionApi = TransactionApiWrapper(requests.call(baseInstance));
const SportsApi = SportsApiWrapper(requests.call(baseInstance));
const IntegrationApi = IntegrationApiWrapper(requests.call(baseInstance));
const MyBetApi = MyBetApiWrapper(requests.call(baseInstance));
const PromoCodeApi = PromoCodeWrapper(requests.call(baseInstance));
const KuponKibicaApi = KuponKibicaWrapper(requests.call(baseInstance));
const BetMatchApi = BetMatchApiWrapper(requests.call(baseInstance));
const RefreshConfigApi = RefreshConfigApiWrapper(requests.call(baseInstance));
const SpecialCategoriesApi = SpecialCategoriesApiWrapper(requests.call(baseInstance));
const DailyChallengeApi = DailyChallengeApiWrapper(requests.call(baseInstance));
const playerProfileApi = playerProfileApiWrapper(requests.call(baseInstance));
const BetslipAnalyticsApi = BetslipAnalyticsApiWrapper(requests.call(baseInstance));

export {
	baseInstance,
	CategoriesApi,
	LiveApi,
	BestSeller,
	LiveBetting,
	LoginApi,
	BettingApi,
	RegistrationApi,
	AuthologicApi,
	GiifTypesApi,
	PasswordApi,
	CMSApi,
	CustomerApi,
	OffersApi,
	TransactionApi,
	SportsApi,
	IntegrationApi,
	MyBetApi,
	PromoCodeApi,
	KuponKibicaApi,
	CommonAPI,
	BetMatchApi,
	RefreshConfigApi,
	SpecialCategoriesApi,
	DailyChallengeApi,
	playerProfileApi,
	BetslipAnalyticsApi,
};
